import {
  type AppealResolution,
  type ViolationAction,
  type ViolationStatus,
} from '@aysar/contracts';

import { type BadgeTone } from '@/shared/components/ui/Badge/Badge.tokens';

/** What a consequence should say, and how loudly. */
export interface ConsequenceCopy {
  /** A `violations:consequence.*` key. Pluralised by i18next where it counts. */
  i18nKey: string;
  /** The interpolated `count`, or `undefined` for a key that takes none. */
  count: number | undefined;
  tone: BadgeTone;
}

/**
 * The two actions that take an ACCOUNT out of service.
 *
 * Deliberately not `isReversibleViolationAction`, which is the tempting import:
 * that predicate is true for `hide_message` too, because a hidden message can be
 * unhidden. What is being asked here is different — did `users.status` move —
 * and hiding a message never touches the account.
 */
const ACCOUNT_ACTIONS: readonly ViolationAction[] = ['suspend', 'ban'];

export function changesAccountStatus(action: ViolationAction): boolean {
  return ACCOUNT_ACTIONS.includes(action);
}

/**
 * Did this decision move `users.status`?
 *
 * Read off the row the api answered with, never inferred from which button was
 * pressed: an overturn on a `warn` reverses nothing, and a dismissal never did
 * anything to reverse. What it gates is the shared `members.status.changed`
 * event, so a subscriber is told about a lifecycle change exactly when there was
 * one (W5).
 */
export function movedAccountStatus(violation: {
  status: ViolationStatus;
  actionTaken: ViolationAction | null;
  appealResolution: AppealResolution | null;
}): boolean {
  if (violation.actionTaken === null || !changesAccountStatus(violation.actionTaken)) {
    return false;
  }

  // Just went on, or just came off. An upheld appeal moved nothing — the
  // consequence was already in force when the member appealed it.
  return violation.status === 'enforced' || violation.appealResolution === 'overturned';
}

/**
 * What a consequence will do, in words the operator can act on.
 *
 * A bare "Suspend" is not a consequence anybody can weigh — a day and a year are
 * the same word — so the length travels with it. And a `suspend` rule carrying
 * no length gets its own sentence rather than an interpolated blank: the
 * contract refuses to create one, but a case judged under an older rule can
 * still be sitting in the queue, and "Suspended for  days" would be the dialog
 * lying at the moment it matters most.
 */
export function consequenceCopy(
  action: ViolationAction,
  suspensionDays: number | null,
): ConsequenceCopy {
  switch (action) {
    case 'warn':
      return { i18nKey: 'consequence.warn', count: undefined, tone: 'neutral' };
    case 'hide_message':
      return { i18nKey: 'consequence.hide_message', count: undefined, tone: 'info' };
    case 'suspend':
      return suspensionDays === null
        ? { i18nKey: 'consequence.suspendNoLength', count: undefined, tone: 'danger' }
        : { i18nKey: 'consequence.suspend', count: suspensionDays, tone: 'warning' };
    case 'ban':
      return { i18nKey: 'consequence.ban', count: undefined, tone: 'danger' };
  }
}
